import sprintf from "sprintf";

import Checkpoint, { ORDER_EXCLUDED } from "./checkpoint";
import { generateRange, sanitizeHtml } from "./util";
import resource from "@core/Util/resources";
import dateHelpers from "../../Util/date-helpers";

export default class TimerCheckpoint extends Checkpoint {
    constructor() {
        super('timer', 'JourneyBuilder_Checkpoint_Timer', 'JourneyBuilder_Checkpoint_Timer_Description', { iconClass: 'fas fa-hourglass-half' });
    }

    buildTooltip(connection) {
        const config = this.readForConnection(connection);

        return {
            title: resource('JourneyBuilder_Checkpoint_Timer'),
            content: this.generateSummary(config)
        }
    }

    /**
     * Generate an HTML summary for the given timer configuration
     * @param {Object} config
     * @param {Object} config.UI
     * @param {String} config.UI.frequencyRecurrenceUnit
     * @param {String} config.UI.dailyFrequencyRecurrenceUnit
     * @param {Number} config.version
     * @param {String} [config.scheduleType]
     * @param {String} [config.date]
     * @param {String} config.time
     * @param {Number} [config.recursInDays]
     * @param {String[]} config.weekdays
     * @param {String} [config.dailyFrequency]
     * @param {String} config.dailyStartTime
     * @param {String} config.dailyEndTime
     * @param {Number} [config.dailyRecursInSeconds]
     * @param {String} [config.startDate]
     * @param {String} [config.endDate]
     * @returns {String}
     */
    generateSummary(config) {
        const scheduleType = config.scheduleType;

        if(!scheduleType) {
            return '';
        }

        const stringifyTime = time => {
            //Time is already stringified starting from V2
            if(config.version >= 2) {
                return time;
            }
            
            const {
                hour = 0,
                minute = 0,
                second = 0
            } = time;

            const toHumanReadableTimePart = num => sanitizeHtml(num.toString().padStart(2, '0'));

            return `${toHumanReadableTimePart(hour)}:${toHumanReadableTimePart(minute)}:${toHumanReadableTimePart(second)}`;
        }

        const getFrequencyText = () =>  {
            if(!config.recursInDays) {
                return '';
            }

            //Having `recursInDays` implicitly means we have a frequencyUnit selected
            const unit = Object.values(this.FREQUENCY_UNITS)
                .find(unit => unit.id === config.UI.frequencyRecurrenceUnit);

            let text = `${resource('JourneyBuilder_TimerSettings_Summary_Every')} ${unit.fromDays(config.recursInDays)} ${unit.recurrenceLabel.toLowerCase()} `;

            const weekdays = Object.values(this.FREQUENCY_WEEKDAYS);

            //Ignore when all weekdays have been selected
            if(
                config.weekdays.length &&
                config.weekdays.length !== weekdays.length
            ) {
                const selectedWeekdayLabels = config.weekdays
                    .map(id => weekdays.find(wd => wd.id === id).label);

                text += `${resource('JourneyBuilder_TimerSettings_Summary_On')} ${selectedWeekdayLabels.join(', ')} `;
            }

            return sanitizeHtml(text);
        }

        const getDailyFrequencyText = () => {
            if(!config.dailyFrequency) {
                return '';
            }

            if(config.dailyFrequency === this.DAILY_FREQUENCIES.ONCE.id) {
                return sanitizeHtml(` ${resource('JourneyBuilder_TimerSettings_Summary_On')} ${stringifyTime(config.dailyStartTime)} `);
            }

            //Assume multiple times has been selected
            let text = '';

            if(config.dailyRecursInSeconds) {
                const unit = Object.values(this.DAILY_RECURRENCE_UNITS)
                    .find(unit => unit.id === config.UI.dailyFrequencyRecurrenceUnit);

                text += `${resource('JourneyBuilder_TimerSettings_Summary_Every')} ${unit.fromSeconds(config.dailyRecursInSeconds)} ${unit.label} `;
            }

            text += `${resource('JourneyBuilder_TimerSettings_Summary_From')} ${stringifyTime(config.dailyStartTime)} ${resource('JourneyBuilder_TimerSettings_Summary_Until')} ${stringifyTime(config.dailyEndTime)} `;

            return sanitizeHtml(text);
        }

        if(scheduleType === this.SCHEDULE_TYPES.ONCE.id) {
            return sprintf(
                resource('JourneyBuilder_TimerSettings_Summary_Once'),
                sanitizeHtml(dateHelpers.convertConfigToInternalFormat(config.date)),
                sanitizeHtml(` ${stringifyTime(config.time)}`)
            );
        }

        //Assume we're of a recurring schedule type
        let text = `${resource('JourneyBuilder_TimerSettings_Summary_Part_RecordsWillProceed')} <b>`;

        text += getFrequencyText();
        text += getDailyFrequencyText();

        text += '</b>';
        text += resource('JourneyBuilder_TimerSettings_Summary_Part_ToNextTouchpoint');
        text += '<b>'

        if(config.startDate) {
            text += sanitizeHtml(` ${resource('JourneyBuilder_TimerSettings_Summary_From')} ${dateHelpers.convertConfigToInternalFormat(config.startDate)}`);
        }

        if(config.endDate) {
            text += sanitizeHtml(` ${resource('JourneyBuilder_TimerSettings_Summary_Until')} ${dateHelpers.convertConfigToInternalFormat(config.endDate)}`);
        }

        text += '.';
        text += '</b>';

        return text;
    }

    get defaultOrder() {
        return 5;
    }

    get SCHEDULE_TYPES() {
        return {
            ONCE: {
                id: 'once',
                get label() { return resource('JourneyBuilder_TimerSettings_ScheduleType_Once'); }
            },
            RECURRING: {
                id: 'recurring',
                get label() { return resource('JourneyBuilder_TimerSettings_ScheduleType_Recurring'); }
            }
        };
    }

    get FREQUENCY_UNITS() {
        return {
            DAILY: {
                id: 'daily',
                get label() { return resource('JourneyBuilder_TimerSettings_FrequencyUnit_Daily'); },

                recurrenceOptions: generateRange(1, 31),
                get recurrenceLabel() { return resource('JourneyBuilder_TimerSettings_FrequencyUnit_Daily_RecurrenceLabel'); },
                toDays: value => value,
                fromDays: value => value
            },
            WEEKLY: {
                id: 'weekly',
                get label() { return resource('JourneyBuilder_TimerSettings_FrequencyUnit_Weekly'); },

                recurrenceOptions: generateRange(1, 51),
                get recurrenceLabel() { return resource('JourneyBuilder_TimerSettings_FrequencyUnit_Weekly_RecurrenceLabel'); },
                toDays: value => value * 7,
                fromDays: value => value / 7
            }
        };
    }

    get FREQUENCY_WEEKDAYS() {
        return {
            MONDAY: {
                id: 'monday',
                get label() { return resource('Day_Monday') }
            },
            TUESDAY: {
                id: 'tuesday',
                get label() { return resource('Day_Tuesday') }
            },
            WEDNESDAY: {
                id: 'wednesday',
                get label() { return resource('Day_Wednesday') }
            },
            THURSDAY: {
                id: 'thursday',
                get label() { return resource('Day_Thursday') }
            },
            FRIDAY: {
                id: 'friday',
                get label() { return resource('Day_Friday') }
            },
            SATURDAY: {
                id: 'saturday',
                get label() { return resource('Day_Saturday') }
            },
            SUNDAY: {
                id: 'sunday',
                get label() { return resource('Day_Sunday') }
            }
        };
    }

    get DAILY_FREQUENCIES() {
        return {
            MULTIPLE_TIMES: {
                id: 'multiple-times',
                get label() { return resource('JourneyBuilder_TimerSettings_DailyFrequency_MultipleTimes'); }
            },
            ONCE: {
                id: 'once',
                get label() { return resource('JourneyBuilder_TimerSettings_DailyFrequency_Once'); }
            }
        };
    }

    get DAILY_RECURRENCE_UNITS() {
        return {
            MINUTE: {
                id: 'minute',
                get label() { return resource('JourneyBuilder_TimerSettings_DailyRecurrenceUnit_Minute'); },

                recurrenceOptions: generateRange(1, 59),
                toSeconds: value => value * 60,
                fromSeconds: value => value / 60
            },
            HOUR: {
                id: 'hour',
                get label() { return resource('JourneyBuilder_TimerSettings_DailyRecurrenceUnit_Hour') },

                recurrenceOptions: generateRange(1, 23),
                toSeconds: value => value * 3600,
                fromSeconds: value => value / 3600
            }
        };
    }
}